import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, ArrowRight, Mail } from 'lucide-react';
/*
  SEO SPECIFICATION:
  Title: MindStream.ing Blog — AI Automation Guides for Business Owners
  Meta Desc: Practical guides, strategies, and insights on automating your business with AI. Written for business owners, not developers. No fluff. Just results.
  Primary KW: AI business automation blog
*/
export function Blog() {
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'AI Automation', 'Business Operations', 'Marketing', 'Case Studies', 'Product'];
  const posts = [{
    title: 'How to Automate Client Onboarding in 24 Hours',
    category: 'AI Automation',
    date: 'April 10, 2026',
    readTime: '8 min',
    excerpt: 'Stop manually sending the same welcome emails and chasing documents. Here is the exact Stream setup to automate your entire intake process.',
    color: 'bg-purple'
  }, {
    title: 'The Real Cost of Not Automating Your Business',
    category: 'Business Operations',
    date: 'April 8, 2026',
    readTime: '6 min',
    excerpt: 'You think you are saving money by doing it yourself. The math says otherwise. Why the "sweat equity" phase needs to end.',
    color: 'bg-teal'
  }, {
    title: 'Why Your CRM Is a Graveyard (And How to Fix It)',
    category: 'Marketing',
    date: 'April 5, 2026',
    readTime: '5 min',
    excerpt: 'If your team has to manually log every call and email, your CRM is already dead. How to make it self-managing.',
    color: 'bg-red-cta'
  }, {
    title: 'From 60-Hour Weeks to 20: A MindStream.ing Case Study',
    category: 'Case Studies',
    date: 'April 2, 2026',
    readTime: '10 min',
    excerpt: 'How one agency owner replaced three open roles with MR and finally took a vacation without checking Slack.',
    color: 'bg-purple-alt'
  }, {
    title: 'Streams vs Workflows: Why the Difference Matters',
    category: 'Product',
    date: 'March 28, 2026',
    readTime: '4 min',
    excerpt: 'Workflows are rigid paths. Streams are intelligent processes that adapt to context. Here is why that changes everything.',
    color: 'bg-steel'
  }, {
    title: 'The Assembly Line Philosophy: Building a Business That Runs Itself',
    category: 'Business Operations',
    date: 'March 25, 2026',
    readTime: '7 min',
    excerpt: "Ford didn't build cars by hand, he built the assembly line. Why your service business needs the exact same approach.",
    color: 'bg-teal'
  }];
  const filteredPosts = activeCategory === 'All' ? posts : posts.filter(post => post.category === activeCategory);
  return <main className="pt-20" data-id="element-1299" data-mp="70ctlxzg729f">
      <section className="section-padding bg-dark-bg min-h-screen relative overflow-hidden" data-id="element-1300" data-mp="23e3rrmthhh1">
        {/* Ambient Glow Orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1301" data-mp="2dq7xlj5z0b4q" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1302" data-mp="2ij9d9te04epd" />

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-1303" data-mp="28s69ukxlnnmx" />

        <div className="inner-container relative z-10" data-id="element-1304" data-mp="2ngj9brvwbom6">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1305" data-mp="2nfzskehzf69q">
            <h1 className="font-poppins font-extrabold text-5xl md:text-6xl leading-[1.1] mb-6" data-id="element-1306" data-mp="2sekqu8dobfvj">
              The Assembly Line.
              <br data-id="element-1307" data-mp="1bzsz7xocqcna" />
              <span className="text-gradient" data-id="element-1308" data-mp="1c3gvh4l6ld5p">Your Reading List.</span>
            </h1>
            <p className="text-lg text-steel" data-id="element-1309" data-mp="1gy49g9ik6p61">
              Practical guides on automating your business with AI. No fluff.
              Just results.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-16" data-id="element-1310" data-mp="1mk0uo978446r">
            {categories.map(category => <button key={category} onClick={() => setActiveCategory(category)} className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === category ? 'bg-white text-dark-bg shadow-[0_0_15px_rgba(255,255,255,0.3)]' : 'bg-white/5 text-steel hover:text-white border border-white/10 backdrop-blur-sm'}`} data-id="element-1311" data-mp="1iipawkb16d6h">
                {category}
              </button>)}
          </div>

          <div className="grid lg:grid-cols-3 gap-8" data-id="element-1312" data-mp="21b9ufb4ub5xm">
            {/* Main Content - Posts Grid */}
            <div className="lg:col-span-2 grid md:grid-cols-2 gap-6" data-id="element-1313" data-mp="ptrw4r612yih">
              {filteredPosts.map((post, i) => <motion.article key={i} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4
            }} className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:-translate-y-1 hover:border-teal/40 hover:shadow-[0_0_30px_rgba(7,133,183,0.15)] transition-all cursor-pointer group flex flex-col h-full" data-id="element-1314" data-mp="31hnarew01t13" data-mpi={i}>
                  <div className="mb-4" data-id="element-1315" data-mp="3u3olr32h3osq">
                    <span className={`${post.color} text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full`} data-id="element-1316" data-mp="229vs3yupem9">
                      {post.category}
                    </span>
                  </div>
                  <h3 className="font-poppins font-bold text-xl mb-3 group-hover:text-teal transition-colors" data-id="element-1317" data-mp="23v6emcz05lgx">
                    {post.title}
                  </h3>
                  <p className="text-steel text-sm mb-6 flex-grow" data-id="element-1318" data-mp="326qtp0s213xf">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-steel border-t border-white/10 pt-4 mt-auto" data-id="element-1319" data-mp="3g2un4rv3t88u">
                    <span data-id="element-1320" data-mp="3q2v4i89exeae">{post.date}</span>
                    <span className="flex items-center gap-1" data-id="element-1321" data-mp="3q1k32f3fwimg">
                      <Clock size={14} data-id="element-1322" data-mp="3l4junxj4b3s6" /> {post.readTime}
                    </span>
                  </div>
                </motion.article>)}
            </div>

            {/* Sidebar */}
            <div className="hidden lg:flex flex-col gap-8" data-id="element-1323" data-mp="3utbo6a3ttw57">
              {/* Newsletter Signup */}
              <div className="bg-gradient-to-br from-teal/10 to-transparent backdrop-blur-sm border border-teal/30 rounded-2xl p-6 relative overflow-hidden shadow-[0_0_30px_rgba(7,133,183,0.1)]" data-id="element-1324" data-mp="cngrrqraq8kb">
                <div className="absolute top-0 right-0 w-32 h-32 bg-teal/20 rounded-full blur-3xl pointer-events-none" data-id="element-1325" data-mp="rafdr67dwarn"></div>
                <div className="relative z-10" data-id="element-1326" data-mp="w8pedwbvo3kn">
                  <div className="bg-teal/20 w-12 h-12 rounded-full flex items-center justify-center text-teal mb-4 shadow-[0_0_15px_rgba(7,133,183,0.3)]" data-id="element-1327" data-mp="ia95pzugg2zv">
                    <Mail size={24} data-id="element-1328" data-mp="ma9izh5ti85c" />
                  </div>
                  <h3 className="font-poppins font-bold text-xl mb-2" data-id="element-1329" data-mp="8jxpbdtl41c3">
                    Get the Playbooks
                  </h3>
                  <p className="text-steel text-sm mb-6" data-id="element-1330" data-mp="dbxtxjklnif0">
                    Join 10,000+ business owners getting our best automation
                    strategies every week.
                  </p>
                  <form className="space-y-3" onSubmit={e => e.preventDefault()} data-id="element-1331" data-mp="3m4j4ksa77bt">
                    <input type="email" placeholder="Enter your email" className="w-full bg-dark-bg/50 border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-teal focus:bg-white/5 transition-colors" data-id="element-1332" data-mp="3qq2m8eosdegx" />
                    <button className="w-full bg-teal text-white font-semibold py-3 rounded-lg hover:bg-teal/90 shadow-[0_0_15px_rgba(7,133,183,0.3)] transition-all" data-id="element-1333" data-mp="3qobrbs13yy">
                      Subscribe
                    </button>
                  </form>
                </div>
              </div>

              {/* Top Posts */}
              <div className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-6" data-id="element-1334" data-mp="3ihpvtsqdm163">
                <h3 className="font-poppins font-bold text-lg mb-4 border-b border-white/10 pb-2" data-id="element-1335" data-mp="38kboqjqceoqg">
                  Top Posts
                </h3>
                <div className="space-y-4" data-id="element-1336" data-mp="34houyda42tqm">
                  {[posts[0], posts[2], posts[4]].map((post, i) => <div key={i} className="group cursor-pointer" data-id="element-1337" data-mp="2zk5ed6pxy4vi" data-mpi={i}>
                      <h4 className="text-sm font-medium group-hover:text-teal transition-colors mb-1" data-id="element-1338" data-mp="1f2e1fxkqhes0">
                        {post.title}
                      </h4>
                      <p className="text-xs text-steel" data-id="element-1339" data-mp="1o29igj0z03zy">{post.readTime} read</p>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}